import { z } from 'zod';

const rawAuthorSchema = z.object({
    id: z.union([z.number(), z.string()]).transform((value) => Number(value)).refine((value) => Number.isFinite(value) && value > 0),
    name: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    username: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    avatar_url: z.string().nullable().optional(),
}).passthrough();

export const blogAuthorSchema = rawAuthorSchema.transform((raw) => {
    const fullName = `${raw.first_name ?? ''} ${raw.last_name ?? ''}`.trim();
    const fallbackName = raw.name ?? (fullName || raw.username || raw.email || `User ${raw.id}`);
    return {
        id: raw.id,
        name: fallbackName,
        avatarUrl: raw.avatar_url ?? null,
    };
});

const rawImageSchema = z.object({
    id: z.number().int().positive(),
    post_id: z.number().int().positive().optional(),
    url: z.string().min(1),
    mime_type: z.string().optional(),
    original_filename: z.string().optional(),
    caption: z.string().nullable().optional(),
    sort_order: z.number().int().optional(),
    uploaded_at: z.string().optional(),
    uploader: rawAuthorSchema.nullish(),
    created_at: z.string().optional(),
}).passthrough();

export const blogImageSchema = rawImageSchema.transform((raw) => ({
    id: raw.id,
    postId: raw.post_id ?? null,
    url: raw.url,
    mimeType: raw.mime_type ?? 'application/octet-stream',
    originalFilename: raw.original_filename ?? `blog-image-${raw.id}`,
    caption: raw.caption ?? '',
    sortOrder: raw.sort_order ?? 0,
    uploadedAt: raw.uploaded_at ?? raw.created_at ?? null,
    uploader: raw.uploader ? blogAuthorSchema.parse(raw.uploader) : null,
    createdAt: raw.created_at ?? raw.uploaded_at ?? null,
}));

const rawTagSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    color: z.string(),
});

const rawPostPreviewSchema = z.object({
    id: z.number().int().positive(),
    title: z.string().min(1),
    teaser: z.string().nullable().optional(),
    excerpt: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    author: rawAuthorSchema,
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    cover_image_url: z.string().nullable().optional(),
    cover_image: z.string().nullable().optional(),
    images: z.array(rawImageSchema).nullable().optional(),
    preview_images: z.array(rawImageSchema).nullable().optional(),
    tags: z.array(rawTagSchema).nullable().optional(),
}).passthrough();

const mapTags = (tags: z.infer<typeof rawTagSchema>[] | null | undefined) =>
    (tags ?? []).map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }));

export const blogPostPreviewSchema = rawPostPreviewSchema.transform((raw) => ({
    id: raw.id,
    title: raw.title,
    teaser: raw.teaser ?? raw.excerpt ?? '',
    fallbackText: raw.content ?? raw.excerpt ?? '',
    author: blogAuthorSchema.parse(raw.author),
    createdAt: raw.created_at ?? '',
    updatedAt: raw.updated_at ?? null,
    coverImageUrl: raw.cover_image_url ?? raw.cover_image ?? null,
    previewImages: (raw.preview_images ?? raw.images ?? []).map((image) => blogImageSchema.parse(image)).slice(0, 4),
    tags: mapTags(raw.tags),
}));

const rawPostDetailSchema = rawPostPreviewSchema.extend({
    content: z.string().default(''),
    images: z.array(rawImageSchema).optional().default([]),
});

export const blogPostDetailSchema = rawPostDetailSchema.transform((raw) => ({
    id: raw.id,
    title: raw.title,
    teaser: raw.teaser ?? raw.excerpt ?? '',
    content: raw.content ?? '',
    author: blogAuthorSchema.parse(raw.author),
    createdAt: raw.created_at ?? '',
    updatedAt: raw.updated_at ?? null,
    coverImageUrl: raw.cover_image_url ?? raw.cover_image ?? null,
    images: raw.images.map((image) => blogImageSchema.parse(image)),
    tags: mapTags(raw.tags),
}));

const rawBlogPostsPageSchema = z.object({
    items: z.array(rawPostPreviewSchema).default([]),
    page: z.number().int().positive().default(1),
    page_size: z.number().int().positive().default(10),
    total: z.number().int().nonnegative().default(0),
});

export const blogPostsPageSchema = rawBlogPostsPageSchema.transform((raw) => ({
    items: raw.items.map((item) => blogPostPreviewSchema.parse(item)),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
}));

export type BlogAuthor = z.infer<typeof blogAuthorSchema>;
export type BlogReactionCounts = { '❤️': number; '👍': number; '🍺': number };
export type BlogReactionKind = '❤️' | '👍' | '🍺';
export type BlogReactionUserNames = Record<BlogReactionKind, string[]>;
export interface BlogReaction {
    userId: number;
    userName: string;
    emoji: BlogReactionKind;
}

// Explicit interface needed to avoid circular z.infer reference (replies: BlogComment[])
export interface BlogComment {
    id: number;
    text: string;
    author: BlogAuthor;
    parentId: number | null;
    createdAt: string;
    updatedAt: string | null;
    counts: BlogReactionCounts;
    viewerEmoji: BlogReactionKind | null;
    reactions: BlogReaction[];
    reactionUserNames: BlogReactionUserNames;
    replies: BlogComment[];
}

const EMPTY_COUNTS: BlogReactionCounts = { '❤️': 0, '👍': 0, '🍺': 0 };
const EMPTY_REACTION_USER_NAMES: BlogReactionUserNames = { '❤️': [], '👍': [], '🍺': [] };

const rawBlogCommentSchema = z.object({
    id: z.number().int().positive(),
    text: z.string().default(''),
    author: rawAuthorSchema.nullish(),
    user: rawAuthorSchema.nullish(),
    parent_id: z.number().int().nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    counts: z.record(z.string(), z.number()).optional(),
    viewer_emoji: z.string().nullable().optional(),
    reactions: z.array(z.object({
        user_id: z.number().int(),
        user_name: z.string().default(''),
        emoji: z.enum(['❤️', '👍', '🍺']),
    })).optional().default([]),
}).passthrough();

export const blogCommentSchema = rawBlogCommentSchema.transform((raw): BlogComment => {
    const reactions: BlogReaction[] = raw.reactions.map((reaction) => ({
        userId: reaction.user_id,
        userName: reaction.user_name,
        emoji: reaction.emoji,
    }));
    const reactionUserNames = reactions.reduce<BlogReactionUserNames>((acc, reaction) => {
        if (reaction.userName) {
            acc[reaction.emoji].push(reaction.userName);
        }
        return acc;
    }, { '❤️': [], '👍': [], '🍺': [] });

    return {
        id: raw.id,
        text: raw.text,
        author: blogAuthorSchema.parse(raw.author ?? raw.user ?? { id: 999999, name: 'Unbekannt' }),
        parentId: raw.parent_id ?? null,
        createdAt: raw.created_at ?? '',
        updatedAt: raw.updated_at ?? null,
        counts: (raw.counts ?? EMPTY_COUNTS) as BlogReactionCounts,
        viewerEmoji: (raw.viewer_emoji ?? null) as BlogReactionKind | null,
        reactions,
        reactionUserNames: reactions.length > 0 ? reactionUserNames : { ...EMPTY_REACTION_USER_NAMES },
        replies: [],
    };
});

const buildCommentTree = (flat: BlogComment[]): BlogComment[] => {
    const byId = new Map<number, BlogComment>();
    flat.forEach((c) => byId.set(c.id, { ...c, replies: [] }));

    const roots: BlogComment[] = [];
    byId.forEach((comment) => {
        if (comment.parentId !== null && byId.has(comment.parentId)) {
            byId.get(comment.parentId)!.replies.push(comment);
        } else {
            roots.push(comment);
        }
    });

    const sort = (items: BlogComment[]): BlogComment[] =>
        items
            .map((item) => ({ ...item, replies: sort(item.replies) }))
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    return sort(roots);
};

export const blogCommentListSchema = z.array(z.unknown()).transform((rawComments) => {
    const flat = rawComments.flatMap((rawComment) => {
        const parsed = blogCommentSchema.safeParse(rawComment);
        return parsed.success ? [parsed.data] : [];
    });
    return buildCommentTree(flat);
});

export const blogPostCreateInputSchema = z.object({
    title: z.string().trim().min(1, 'Titel ist erforderlich.'),
    teaser: z.string().trim().max(300, 'Teaser darf maximal 300 Zeichen haben.').optional().default(''),
    content: z.string().trim().min(1, 'Inhalt ist erforderlich.'),
    tag_ids: z.array(z.number().int().positive()).optional().default([]),
});

export const blogPostUpdateInputSchema = blogPostCreateInputSchema.partial().extend({
    id: z.number().int().positive(),
});

export const blogCommentCreateInputSchema = z.object({
    text: z.string().trim().min(1, 'Kommentar darf nicht leer sein.'),
    parent_id: z.number().int().nullable().optional(),
});

export const blogPostsQueryInputSchema = z.object({
    page: z.number().int().positive().default(1),
    pageSize: z.number().int().positive().max(50).default(10),
});

export type BlogImage = z.infer<typeof blogImageSchema>;
export type BlogPostPreview = z.infer<typeof blogPostPreviewSchema>;
export type BlogPostDetail = z.infer<typeof blogPostDetailSchema>;
export type BlogPostsPage = z.infer<typeof blogPostsPageSchema>;
export type BlogPostCreateInput = z.infer<typeof blogPostCreateInputSchema>;
export type BlogPostUpdateInput = z.infer<typeof blogPostUpdateInputSchema>;
export type BlogCommentCreateInput = z.infer<typeof blogCommentCreateInputSchema>;
